import React, { useState, useEffect, useMemo, useRef } from 'react';
// Schedule view pulls SCHEDULE sections, not SOV sections (those are
// fetched separately by SOVTab). Different concepts, different tables.
import { useLocation } from 'react-router-dom';
import { getTasks, getScheduleSections, getDependencyAlerts, getProject, getProjectMoves, getProjectWorkProgress, getProgressWaiting } from '../api';
import TasksTab, { TASK_LIST_MIN_W } from './TasksTab';
import GanttTab from './GanttTab';
import TaskEditModal from './TaskEditModal';
import PrintScheduleModal from './PrintScheduleModal';
import PrintScheduleView from './PrintScheduleView';
import { SCHEDULE_SORT_OPTIONS, classifyTaskStatus } from './scheduleSort';
import { usePageSearchState, matchesSearch } from '../lib/pageSearch';
import { useProjectToday } from '../lib/projectToday';
import SubMovesBanner from './subs/SubMovesBanner';
import SubProgressBanner, { withLive } from './subs/SubProgressBanner';
import SubReportsBanner, { isCloseoutDocs } from './subs/SubReportsBanner';
import JobHeader from './JobHeader';
import { rollUp } from '../lib/rollup';

/* Subtasks (Duncan 2026-10-29/30, the PM meeting; server/subtasks.js): one level. A task's subtasks follow it in date order
   ("Subtasks stay in date order under X"); it shows their span and their % by working days -- figures Summit keeps on the
   parent row -- and it holds no dollars, sub or arrows itself. `_kids` marks a parent (how many), `_depth` a subtask. */
function nestSubtasks(list) {
  const ids = new Set(list.map((t) => t.id));
  const kidsOf = new Map();
  list.forEach((t) => { if (t.parent_task_id != null && ids.has(t.parent_task_id)) kidsOf.set(t.parent_task_id, [...(kidsOf.get(t.parent_task_id) || []), t]); });
  if (!kidsOf.size) return list;
  const out = [];
  list.forEach((t) => {
    if (t.parent_task_id != null && ids.has(t.parent_task_id)) return;
    const kids = (kidsOf.get(t.id) || []).slice().sort((a, b) => (a.start_date || '9999').localeCompare(b.start_date || '9999') || a.id - b.id);
    if (!kids.length) { out.push(t); return; }
    out.push({ ...t, _kids: kids.length });
    kids.forEach((k) => out.push({ ...k, _depth: 1 }));
  });
  return out;
}
/** After a filter or sort: each parent's subtasks right under it (in code order), none under a closed one, then the list's
    "+ Add a subtask" row (the Gantt keeps an empty row there, so both stay in line). A subtask the filter kept brings its
    parent along, so it's never shown without it. */
const renest = (list, all, closed) => {
  const shown = new Set(list.map((t) => t.id));
  const byId = new Map(all.map((t) => [t.id, t]));
  const parentsNeeded = list.filter((t) => t._depth && !shown.has(t.parent_task_id)).map((t) => byId.get(t.parent_task_id)).filter(Boolean);
  const tops = [...list.filter((t) => !t._depth), ...parentsNeeded.filter((p, i, a) => a.indexOf(p) === i)];
  const kids = new Map();
  list.forEach((t) => { if (t._depth) kids.set(t.parent_task_id, [...(kids.get(t.parent_task_id) || []), t]); });
  const out = [];
  tops.forEach((t) => {
    out.push(t);
    if (!t._kids || closed[t.id]) return;
    (kids.get(t.id) || []).sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true })).forEach((k) => out.push(k));
    out.push({ id: `add-${t.id}`, _addFor: t.id, building_area_phase: t.building_area_phase, task_code: '', task_name: '', code: '', name: '' });
  });
  return out;
};

const SCHEDULE_SPLIT_KEY = 'e3.scheduleSplitPct';
// The Gantt's least when the task list takes the room its columns need (a small screen: Duncan 2026-10-22).
const GANTT_MIN_W = 320;

// Parent for the combined Schedule tab. Owns the shared task list,
// sections, collapse state and dep alerts so Tasks and Gantt always
// render the same rows in the same order.
const ScheduleView = ({ projectId, refreshSummary, onTaskOpen, refreshKey, workWeek = '5' }) => {
  const [tasks, setTasks] = useState([]);
  const [savedSections, setSavedSections] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [collapsedParents, setCollapsedParents] = useState({}); // subtasks hidden under these tasks
  const [depAlerts, setDepAlerts] = useState({ alerts: [], flagged_task_ids: [], summary: {} });
  const [loading, setLoading] = useState(true);
  // Schedule-wide search query lives here so both the Tasks panel AND
  // the Gantt panel filter in lockstep — otherwise the rows would
  // misalign between the two scrollable sides when the user is
  // hunting for a specific task. Shared with the search bar at the top of
  // the project (one text), so TasksTab's "Search tasks…" box is the bar's.
  const [scheduleQuery, setScheduleQuery] = usePageSearchState('Search tasks…');
  // Filter state — three independent dimensions, AND-combined.
  // Empty Set on any dimension means "no constraint" for that dimension.
  //   scheduleFilter.sections — multi-select of section names visible
  //     in this project. Section header for an excluded section is
  //     dropped from visibleRows entirely (no empty headers).
  //   scheduleFilter.statuses — subset of:
  //       'Not Started' | 'In Progress' | 'Complete'
  //     Classified from task.status (falls back to deriving from
  //     percent_complete: 0 = Not Started, 100 = Complete, else In Progress).
  //   scheduleFilter.types — subset of the seven task_type values
  //     (Labor / Material / Procurement / Engineering / Inspection /
  //     Documents / Milestone).
  const [scheduleFilter, setScheduleFilter] = useState({
    sections: new Set(),
    statuses: new Set(),
    types: new Set(),
  });
  // Sort key referencing SCHEDULE_SORT_OPTIONS above. Applied
  // within-section so cross-section AIA grouping is preserved.
  const [scheduleSort, setScheduleSort] = useState('default');
  // Local edit modal state: when a Gantt bar is double-clicked, GanttTab calls
  // onTaskOpen which sets this id, opening TaskEditModal with the shared tasks.
  const [editTaskId, setEditTaskId] = useState(null);
  // The task window's "Add a subtask" (2026-11-02): it closes, and TasksTab opens Add Task already a subtask of it.
  const [addSubFor, setAddSubFor] = useState(null);
  // 'progress' when the sub progress banner opened it: Task Properties then leads to the dates and the % bar.
  const [editFocus, setEditFocus] = useState(null);

  // Print Schedule state — opened from TasksTab's toolbar.
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printConfig, setPrintConfig] = useState(null); // { from, to, label, rangeType }
  const [projectName, setProjectName] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  useEffect(() => {
    if (!projectId) return;
    getProject(projectId).then(p => {
      setProjectName(p?.name || p?.project_name || '');
      setProjectNumber(p?.project_number || '');
    }).catch(() => {});
  }, [projectId]);

  const refreshTasks = async () => {
    try {
      const data = await getTasks(projectId);
      setTasks(data);
    } catch (e) { console.error('Error fetching tasks (shared):', e); }
  };
  const refreshSections = async () => {
    try {
      const data = await getScheduleSections(projectId);
      setSavedSections(data);
    } catch (e) { console.error('Error fetching sections (shared):', e); }
  };
  const refreshAlerts = async () => {
    try {
      const data = await getDependencyAlerts(projectId);
      setDepAlerts(data);
    } catch (e) { console.error('Error fetching dep alerts (shared):', e); }
  };

  // Subs' date moves waiting on the PM (2026-10-24; subs/SubMovesBanner.js): read from E3 Subs on open, every
  // 5 minutes while the tab is open, and after each answer -- never holding up the schedule itself.
  const [subMoves, setSubMoves] = useState([]);
  const refreshMoves = async () => {
    try {
      const data = await getProjectMoves(projectId);
      setSubMoves((data && data.moves) || []);
    } catch (e) { /* E3 Subs or the job unreachable: no banner */ }
  };
  // Subs' progress waiting on the PM (2026-10-28; subs/SubProgressBanner.js): the same way, and after each answer --
  // and when a test job's test date moves (what its dates allow moves with it).
  // And each sub's photo reports waiting for a closeout Documents task (2026-10-29; subs/SubReportsBanner.js), from the
  // same read.
  const [subProgress, setSubProgress] = useState([]);
  const [subReports, setSubReports] = useState([]);
  const refreshProgress = async () => {
    try {
      const data = await getProgressWaiting(projectId);
      setSubProgress((data && data.items) || []);
      setSubReports((data && data.reports) || []);
    } catch (e) { /* E3 Subs or the job unreachable: no banner */ }
  };
  const { testDate } = useProjectToday();
  const testDateSeen = useRef(testDate);
  useEffect(() => {
    if (testDateSeen.current === testDate) return;
    testDateSeen.current = testDate;
    if (projectId) refreshProgress();
    // eslint-disable-next-line
  }, [testDate]);
  // Who each sub put on its tasks, from E3 Subs (2026-10-24; names only): { [taskId]: [{ name, since }] }. And the
  // documents on each Documents task (its count; absent until E3 Subs sends it): { [taskId]: n } -- clears its flag.
  const [technicians, setTechnicians] = useState({});
  const [documents, setDocuments] = useState({});
  const refreshTechnicians = async () => {
    try {
      const p = await getProjectWorkProgress(projectId);
      const map = {};
      const docs = {};
      Object.entries((p && p.on && p.tasks) || {}).forEach(([id, t]) => {
        if (t && t.technicians && t.technicians.length) map[id] = t.technicians;
        if (t && Number.isFinite(t.documents)) docs[id] = t.documents;
      });
      setTechnicians(map);
      setDocuments(docs);
    } catch (e) { /* E3 Subs unreachable: no names */ }
  };
  useEffect(() => {
    if (!projectId) return undefined;
    refreshMoves();
    refreshTechnicians();
    refreshProgress();
    const timer = setInterval(() => { refreshMoves(); refreshTechnicians(); refreshProgress(); }, 5 * 60 * 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [projectId, refreshKey]);
  // ?move=<id> (the bell, and E3 Subs' summary email): that move, highlighted and scrolled to.
  const location = useLocation();
  const [focusMoveId, setFocusMoveId] = useState(null);
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('move');
    if (id) setFocusMoveId(id);
  }, [location.search]);
  // ?progress=<taskId> or ?progress=all (the bell): the progress banner, scrolled to (that task highlighted).
  const [focusProgress, setFocusProgress] = useState(null);
  useEffect(() => {
    const p = new URLSearchParams(location.search).get('progress');
    if (p) setFocusProgress(p);
  }, [location.search]);
  useEffect(() => {
    if (focusProgress !== 'all' || !subProgress.length) return;
    const el = document.getElementById('sub-progress');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusProgress, subProgress.length]);
  // ?reports=<subId> or ?reports=all (the bell, and a sub's card on the Subcontractors tab): the photo-reports banner,
  // scrolled to (that sub highlighted).
  const [focusReports, setFocusReports] = useState(null);
  useEffect(() => {
    const r = new URLSearchParams(location.search).get('reports');
    if (r) setFocusReports(r);
  }, [location.search]);
  useEffect(() => {
    if (focusReports !== 'all' || !subReports.length) return;
    const el = document.getElementById('sub-reports');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusReports, subReports.length]);
  // A sub that now has its closeout Documents task: E3 Subs files its reports within a minute or two, so look again each
  // minute until they're filed (ten times at most).
  const filingTries = useRef(0);
  const filing = subReports.some((r) => r.closeout_task
    || tasks.some((t) => Number(t.subcontractor_id) === Number(r.sub_id) && isCloseoutDocs(t)));
  useEffect(() => {
    if (!filing) { filingTries.current = 0; return undefined; }
    if (filingTries.current >= 10) return undefined;
    const timer = setTimeout(() => { filingTries.current += 1; refreshProgress(); }, 60 * 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [filing, subReports]);
  // Add Task, opened already filled in from a banner (TasksTab sets this to its opener).
  const addTaskRef = useRef(null);
  // Subs' progress against the schedule's % as it stands now, by task (2026-10-28): the Gantt's stripes and tag, the
  // list's pill and Task Properties' note read it; the banner reads the same way (livePct).
  const livePct = (taskId) => {
    const t = tasks.find((x) => x.id === taskId);
    return t ? Math.max(0, Math.min(100, Math.round(Number(t.percent_complete) || 0))) : null;
  };
  const subProgressByTask = useMemo(() => {
    const map = {};
    subProgress.forEach((x) => { const live = withLive(x, livePct(x.task_id)); if (live) map[x.task_id] = live; });
    return map;
    // eslint-disable-next-line
  }, [subProgress, tasks]);
  // By task: each move, and the sub's own tasks that moved along with it (their own dates; answered with it).
  const subMovesByTask = useMemo(() => {
    const map = {};
    subMoves.forEach((m) => {
      (m.cascade || []).forEach((c) => { map[c.taskId] = { ...m, taskId: c.taskId, start: c.start, finish: c.finish }; });
      map[m.taskId] = m;
    });
    return map;
  }, [subMoves]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([refreshTasks(), refreshSections(), refreshAlerts()]).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [projectId, refreshKey]);

  // Group by section using the same fallback name + same within-section sort
  // as TasksTab. This is the single source of truth.
  const groupedTasks = useMemo(() => {
    const groups = {};
    tasks.forEach(task => {
      const section = task.building_area_phase || 'Unsectioned';
      if (!groups[section]) groups[section] = [];
      groups[section].push(task);
    });
    Object.keys(groups).forEach(section => {
      groups[section].sort((a, b) => (a.task_code || '').localeCompare(b.task_code || '', undefined, { numeric: true }));
      groups[section] = nestSubtasks(groups[section]);
    });
    return groups;
  }, [tasks]);

  // Add gantt-style aliases (code/name/end_date/...) on each task so GanttTab
  // can read either field name without further changes inside that component.
  const aliasGantt = (t) => ({
    ...t,
    code: t.task_code,
    name: t.task_name,
    end_date: t.finish_date,
    duration: t.duration_days,
    progress: t.percent_complete,
    parent_id: t.parent_task_id,
    is_milestone: t.milestone_flag,
    dependencies: t.predecessors || [],
  });

  const sections = useMemo(() => {
    const savedNames = savedSections.map(s => s.section_name);
    const savedSet = new Set(savedNames);
    const allSectionNames = new Set([...savedNames, ...Object.keys(groupedTasks)]);
    const unsavedNames = Array.from(allSectionNames).filter(n => !savedSet.has(n)).sort();
    return [...savedNames.filter(n => allSectionNames.has(n)), ...unsavedNames];
  }, [savedSections, groupedTasks]);

  // Display-positional task_code override. The server already returns
  // task_code as TSK-NNNN based on global sort_order rank, but the
  // visible list groups by section -- so a Closeout section showing up
  // first alphabetically would still display TSK-0003 as its first row
  // (because tasks #1 and #2 belong to other sections that sort lower
  // in sort_order). Users see "TSK-0003 at the top" and read that as
  // wrong.
  // Rewrite task_code AT THE DISPLAY LAYER: iterate sections in the
  // order they'll appear on screen, then tasks within each section,
  // and stamp TSK-0001, TSK-0002, ... in that flat sequence. The new
  // codes propagate everywhere because predecessor / SOV-link /
  // dependent-task lookups all find tasks by id and read whatever
  // task_code came back. Both this tasks array AND groupedTasks need
  // the same codes so neither view drifts.
  const displayCodeMap = useMemo(() => {
    const map = new Map();
    let idx = 0;
    sections.forEach(section => {
      let kid = 0;
      (groupedTasks[section] || []).forEach(t => {
        // a subtask is its task's code and its place under it: TSK-0037.1 (server/taskCodes.js codesOf)
        if (t._depth) { kid += 1; map.set(t.id, `TSK-${String(idx).padStart(4, '0')}.${kid}`); return; }
        idx += 1;
        kid = 0;
        map.set(t.id, `TSK-${String(idx).padStart(4, '0')}`);
      });
    });
    return map;
  }, [sections, groupedTasks]);

  const tasksWithDisplayCodes = useMemo(
    () => tasks.map(t => ({ ...t, task_code: displayCodeMap.get(t.id) || t.task_code })),
    [tasks, displayCodeMap]
  );
  const groupedTasksWithDisplayCodes = useMemo(() => {
    const out = {};
    Object.entries(groupedTasks).forEach(([section, list]) => {
      out[section] = list.map(
        t => ({ ...t, task_code: displayCodeMap.get(t.id) || t.task_code })
      );
    });
    return out;
  }, [groupedTasks, displayCodeMap]);

  const ganttTasks = useMemo(
    () => tasksWithDisplayCodes.map(aliasGantt),
    [tasksWithDisplayCodes]
  );
  const groupedGanttTasks = useMemo(() => {
    const out = {};
    Object.entries(groupedTasksWithDisplayCodes).forEach(([section, list]) => {
      out[section] = list.map(aliasGantt);
    });
    return out;
  }, [groupedTasksWithDisplayCodes]);

  // Pre-compute the filtered + sorted groupedGanttTasks map. Both
  // panels (Tasks list AND Gantt) read from this, so the rows stay
  // in lockstep when the user toggles filters or sort.
  //
  // Filter layers (AND-combined):
  //   1. Search box (scheduleQuery) — code / name / building_area_phase
  //   2. Section filter — section name must be in the set (empty = all)
  //   3. Status filter — task's classified status must be in the set
  //   4. Type filter — task_type must be in the set
  const filteredGroupedGanttTasks = useMemo(() => {
    const q = scheduleQuery.trim().toLowerCase();
    const sectionSet = scheduleFilter.sections;
    const statusSet  = scheduleFilter.statuses;
    const typeSet    = scheduleFilter.types;
    const sortOpt = SCHEDULE_SORT_OPTIONS.find((o) => o.key === scheduleSort)
                    || SCHEDULE_SORT_OPTIONS[0];
    const defOpt = SCHEDULE_SORT_OPTIONS[0];

    const matches = (t, section) => {
      // 1. Search
      // (every word typed, in any of the fields -- the same matching as every page's search)
      if (q && !matchesSearch(q, t.task_code || t.code, t.task_name || t.name, t.building_area_phase)) return false;
      // 2. Section filter (section is the group key, not the task's
      //    building_area_phase, because tasks group by their section
      //    name and we want the FILTER to act on the same grouping)
      if (sectionSet.size > 0 && !sectionSet.has(section)) return false;
      // 3. Status filter
      if (statusSet.size > 0 && !statusSet.has(classifyTaskStatus(t))) return false;
      // 4. Type filter
      if (typeSet.size > 0 && !typeSet.has(t.task_type || '')) return false;
      return true;
    };

    const out = {};
    Object.entries(groupedGanttTasks).forEach(([section, list]) => {
      const filtered = list.filter((t) => matches(t, section));
      // Sort within section. Final tiebreaker = default code sort so
      // equal-value rows still group sensibly when not using default.
      const sorted = filtered.slice().sort((a, b) => {
        const primary = sortOpt.cmp(a, b);
        if (primary !== 0) return primary;
        return sortOpt === defOpt ? 0 : defOpt.cmp(a, b);
      });
      out[section] = renest(sorted, list, collapsedParents);
    });
    return out;
  }, [groupedGanttTasks, scheduleQuery, scheduleFilter, scheduleSort, collapsedParents]);

  // Sister structure for the non-gantt-aliased rendering path used by
  // TasksTab. Same filtering/sort, same task ids, same display codes —
  // just without the .code/.name/.end_date aliases.
  const filteredGroupedTasks = useMemo(() => {
    const out = {};
    Object.entries(filteredGroupedGanttTasks).forEach(([section, list]) => {
      // Reverse the gantt-alias by reading from the existing
      // groupedTasksWithDisplayCodes map (which already has both raw
      // and display codes). We just keep the task objects in the same
      // order this filtered list dictates.
      const byId = new Map(
        (groupedTasksWithDisplayCodes[section] || []).map((t) => [t.id, t]),
      );
      out[section] = list.map((aliased) => (aliased._addFor ? aliased : byId.get(aliased.id))).filter(Boolean); // and the add rows
    });
    return out;
  }, [filteredGroupedGanttTasks, groupedTasksWithDisplayCodes]);

  // Filtered + sorted sections list.
  //
  // Two things happen here:
  //   1. Drop sections that have zero matching tasks WHEN any filter /
  //      search is active. With no filters, the full sections list is
  //      preserved so empty sections (typical for new projects) still
  //      show their headers + drop targets.
  //   2. When a non-default sort is active, REORDER the sections
  //      themselves so the globally-extreme task (e.g. earliest start
  //      date) ends up at the very top of the visible list — not just
  //      at the top of its own section. Each section is keyed by its
  //      first task (already sorted within section by the user's
  //      chosen cmp), so comparing first-of-A vs first-of-B with the
  //      same cmp ranks sections by representative value. Sections
  //      with no matching tasks sort to the end.
  const filteredSections = useMemo(() => {
    const hasAnyFilter =
      scheduleQuery.trim() !== '' ||
      scheduleFilter.sections.size > 0 ||
      scheduleFilter.statuses.size > 0 ||
      scheduleFilter.types.size > 0;
    let result = hasAnyFilter
      ? sections.filter((s) => (filteredGroupedGanttTasks[s] || []).length > 0)
      : sections;

    if (scheduleSort !== 'default') {
      const sortOpt = SCHEDULE_SORT_OPTIONS.find((o) => o.key === scheduleSort);
      if (sortOpt) {
        result = [...result].sort((a, b) => {
          const taskA = (filteredGroupedGanttTasks[a] || [])[0];
          const taskB = (filteredGroupedGanttTasks[b] || [])[0];
          if (!taskA && !taskB) return 0;
          if (!taskA) return 1;   // empty section to the end
          if (!taskB) return -1;
          return sortOpt.cmp(taskA, taskB);
        });
      }
    }
    return result;
  }, [sections, filteredGroupedGanttTasks, scheduleQuery, scheduleFilter, scheduleSort]);

  // Each task row carries the gantt-aliased task object so both panels can
  // read row.task directly. Aliased tasks include both the original field names
  // (task_code/task_name/finish_date) and the gantt aliases (code/name/end_date).
  const visibleRows = useMemo(() => {
    const rows = [];
    filteredSections.forEach((section) => {
      const sectionTasks = filteredGroupedGanttTasks[section] || [];
      rows.push({ type: 'section', section });
      if (!collapsedSections[section]) {
        sectionTasks.forEach((task) => {
          rows.push({ type: 'task', section, task });
        });
      }
    });
    return rows;
  }, [filteredSections, filteredGroupedGanttTasks, collapsedSections]);

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Project-wide earliest start / latest finish across all tasks (used to seed
  // the print range picker with the project's full duration).
  const projectDates = useMemo(() => {
    let minS = null;
    let maxE = null;
    tasks.forEach(t => {
      if (t.start_date && (!minS || t.start_date < minS)) minS = t.start_date;
      const end = t.finish_date || t.start_date;
      if (end && (!maxE || end > maxE)) maxE = end;
    });
    return { start: minS, end: maxE };
  }, [tasks]);

  // The whole job, above the columns: its dates and its % by working days (lib/rollup.js, as each section's).
  const job = useMemo(() => (tasks.length ? { name: projectName, ...rollUp(tasks, workWeek) } : null), [tasks, projectName, workWeek]);

  const sharedSchedule = {
    // Surface the display-coded variants so TasksTab/GanttTab/etc. see
    // task_code values that match the visible position. The raw `tasks`
    // (with server-rank codes) is no longer exposed -- consumers
    // shouldn't accidentally read stale codes.
    tasks: tasksWithDisplayCodes,
    setTasks,
    ganttTasks,
    savedSections,
    setSavedSections,
    // Both the raw and filtered groupings flow through. TasksTab uses
    // the filtered ones for its sections.map loop so the search /
    // filter / sort apply there too; the raw ones stay available for
    // drag-and-drop section reordering and any other code that needs
    // the canonical unfiltered list.
    groupedTasks: groupedTasksWithDisplayCodes,
    groupedGanttTasks,
    filteredGroupedTasks,
    filteredGroupedGanttTasks,
    sections,
    filteredSections,
    visibleRows,
    collapsedSections,
    setCollapsedSections,
    toggleSection,
    // A parent's subtasks open and close under it (nestSubtasks above).
    collapsedParents,
    toggleParent: (id) => setCollapsedParents((p) => ({ ...p, [id]: !p[id] })),
    workWeek, // the job's calendar, for the panels' roll-ups (lib/rollup.js)
    depAlerts,
    refreshTasks,
    refreshSections,
    refreshAlerts,
    loading,
    openPrintModal: () => setPrintModalOpen(true),
    scheduleQuery,
    setScheduleQuery,
    scheduleFilter,
    setScheduleFilter,
    scheduleSort,
    setScheduleSort,
    // A sub's date move waiting on the PM, by task id; openMove scrolls the banner to it.
    subMoves: subMovesByTask,
    technicians,
    documents,
    openMove: (moveId) => {
      setFocusMoveId(null);
      setTimeout(() => setFocusMoveId(moveId), 0);
    },
    // A sub's progress waiting on the PM, by task id; openProgress scrolls the banner to it (2026-10-28), and
    // openTaskProgress opens the task itself at its dates, % and the sub's note -- the list's pill and the Gantt's tag
    // (Duncan 2026-10-28: "If I'm clicking there, obviously I want to open the task properties").
    subProgress: subProgressByTask,
    openProgress: (taskId) => {
      setFocusProgress(null);
      setTimeout(() => setFocusProgress(String(taskId)), 0);
    },
    openTaskProgress: (taskId) => { setEditFocus('progress'); setEditTaskId(taskId); },
    // TasksTab's Add Task, for a banner to open already filled in (2026-10-29: a sub's closeout Documents task).
    addTaskRef,
  };

  // Resizable split (lifted from the old inline ScheduleSplitView)
  const [leftPct, setLeftPct] = useState(() => {
    const stored = parseFloat(localStorage.getItem(SCHEDULE_SPLIT_KEY));
    return Number.isFinite(stored) && stored >= 15 && stored <= 85 ? stored : 40;
  });
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftPct(Math.max(15, Math.min(85, pct)));
    };
    const onUp = () => {
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        localStorage.setItem(SCHEDULE_SPLIT_KEY, String(leftPct));
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [leftPct]);

  const startDrag = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  return (
    <>
    <SubMovesBanner
      moves={subMoves}
      codeOf={(taskId) => displayCodeMap.get(taskId) || null}
      focusId={focusMoveId}
      onAnswered={() => { refreshMoves(); }}
      onTaskChanged={() => { refreshTasks(); refreshAlerts(); refreshSummary && refreshSummary(); }}
    />
    <SubProgressBanner
      items={subProgress}
      codeOf={(taskId) => displayCodeMap.get(taskId) || null}
      livePct={livePct}
      focused={focusProgress && focusProgress !== 'all' ? Number(focusProgress) : null}
      onChanged={() => { refreshProgress(); }}
      onTaskChanged={() => { refreshTasks(); refreshAlerts(); refreshSummary && refreshSummary(); }}
      onOpenTask={(taskId) => { setEditFocus('progress'); setEditTaskId(taskId); }}
    />
    <SubReportsBanner
      reports={subReports}
      tasks={tasks}
      codeOf={(taskId) => displayCodeMap.get(taskId) || null}
      focused={focusReports}
      onAddTask={(prefill) => { if (addTaskRef.current) addTaskRef.current(prefill); }}
      onChanged={() => { refreshProgress(); }}
    />
    {/* Sits inside .app-main's 24px gutter so the split-panel aligns
        with the chrome above. Tasks + Gantt internal overflow handlers
        take care of any horizontal scroll their own grids need. */}
    <JobHeader job={job} workWeek={workWeek} />
    <div ref={containerRef} style={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>
      {/* overflowX: 'auto' (was 'clip') so the task table gets a horizontal
          scrollbar when columns exceed the splitter width — otherwise the
          right-most columns are unreachable when the user drags the
          splitter narrow. minWidth: the list never gets narrower than its
          columns while the Gantt can keep GANTT_MIN_W (a small screen gave
          the task name nothing); smaller still, it scrolls. */}
      <div style={{ width: `${leftPct}%`, minWidth: `min(${TASK_LIST_MIN_W}px, calc(100% - ${GANTT_MIN_W}px))`, overflowX: 'auto' }}>
        <TasksTab
          projectId={projectId}
          refreshSummary={refreshSummary}
          sharedSchedule={sharedSchedule}
          workWeek={workWeek}
          // Without this, double-clicking a row in the task list panel
          // falls back to TasksTab's local inline modal -- which has a
          // preview-only SOV Links section (no add UI) and a slimmer
          // overall layout. Passing onTaskOpen routes the click to the
          // SAME TaskEditModal that GanttTab uses, so both panels of
          // the Schedule view open the identical properties dialog with
          // the full <TaskSOVLinks> component.
          onTaskOpen={(id) => { setEditFocus(null); setEditTaskId(id); }}
          addSubtaskFor={addSubFor}
          onAddSubtaskOpened={() => setAddSubFor(null)}
        />
      </div>
      <SplitHandle onMouseDown={startDrag} />
      <div style={{ width: `${100 - leftPct}%`, minWidth: 0 }}>
        <GanttTab
          projectId={projectId}
          refreshSummary={refreshSummary}
          onTaskOpen={(id) => { setEditFocus(null); setEditTaskId(id); }}
          refreshKey={refreshKey}
          inSplitView
          sharedSchedule={sharedSchedule}
        />
      </div>

      {editTaskId && (
        <TaskEditModal
          taskId={editTaskId}
          projectId={projectId}
          workWeek={workWeek}
          availableTasks={tasks}
          subProgress={subProgress.find((x) => x.task_id === editTaskId) || null}
          focus={editFocus}
          onClose={() => setEditTaskId(null)}
          onAddSubtask={(id) => { setEditTaskId(null); setAddSubFor(id); }}
          onSaved={() => { refreshTasks(); refreshAlerts(); refreshSummary && refreshSummary(); refreshProgress(); }}
          onDeleted={() => { setEditTaskId(null); refreshTasks(); refreshAlerts(); refreshSummary && refreshSummary(); }}
          // Fires every time the user adds/removes an SOV link inside
          // the embedded TaskSOVLinks section. refreshTasks re-fetches
          // the task list AND repopulates the taskAllocations map in
          // TasksTab, so the SOV-link badges on each row stay in sync
          // even if the user closes the modal via X (no save).
          onAllocationsChanged={() => { refreshTasks(); refreshSummary && refreshSummary(); }}
        />
      )}

      <PrintScheduleModal
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        onConfirm={(cfg) => { setPrintConfig(cfg); setPrintModalOpen(false); }}
        projectStart={projectDates.start}
        projectEnd={projectDates.end}
      />

      {printConfig && (
        <PrintScheduleView
          projectName={projectName}
          projectNumber={projectNumber}
          rangeLabel={printConfig.label}
          from={printConfig.from}
          to={printConfig.to}
          tasks={tasks}
          sections={sections}
          groupedTasks={groupedTasks}
          onClose={() => setPrintConfig(null)}
        />
      )}
    </div>
    </>
  );
};

export default ScheduleView;

/* -------------------- SplitHandle --------------------
 * Schedule handoff §6 "Splitter":
 *   - 7px wide hot zone, cursor: col-resize
 *   - On hover/drag, reveal a 3px × 32px pill at 30% mix of --accent
 *     and --border-default
 */
function SplitHandle({ onMouseDown }) {
  const [hover, setHover] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const active = hover || dragging;
  return (
    <div
      onMouseDown={(e) => { setDragging(true); onMouseDown(e); }}
      onMouseUp={() => setDragging(false)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title="Drag to resize"
      style={{
        alignSelf: 'stretch',
        width: 7,
        flexShrink: 0,
        cursor: 'col-resize',
        background: 'transparent',
        position: 'relative',
        // No persistent borders — the hover pill is the only visual.
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 3,
          height: 32,
          borderRadius: 2,
          background: 'color-mix(in oklch, var(--accent) 30%, var(--border-default))',
          opacity: active ? 1 : 0,
          transition: 'opacity .12s',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
